import { useState, useEffect, useCallback } from 'react';
import { useDataVersion } from '../lib/dataVersion';

/**
 * Tiny data-fetch hook. Pass a function that returns a promise; re-runs when
 * any value in `deps` changes. Returns { data, loading, error, reload }.
 *
 * It also re-runs when a LightSpeed sync brings in new data (the data version
 * bumps), so a page left open shows today's figures without a reload.
 */
export function useApi(fetcher, deps = []) {
  const version = useDataVersion();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.resolve(fetcher())
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version]);

  useEffect(run, [run]);
  return { data, loading, error, reload: run };
}

export default useApi;
